import { collection, query, where, orderBy, limit, onSnapshot, Timestamp, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import { COLLECTIONS } from '../../constants/collections';
import { VEHICLE_STATUS, STATUS_LABELS, STATUS_COLORS } from '../../constants/statuses';
import { getGeneralSettings } from '../firestore/systemConfigService';
import { getAllDocks } from '../firestore/dockService';

/**
 * Subscribe to dashboard statistics (Real-time)
 * Returns an unsubscribe function
 */
export const subscribeToDashboardStats = (tenantId, callback, scope = null) => {
    if (!tenantId) {
        callback({ pendingReservations: 0, vehiclesAtSite: 0, delayedReservations: 0, efficiency: 0 });
        return () => { };
    }

    let currentUnsubscribe = null;
    let isCancelled = false;

    // Fetch system settings and docks first, then start listener
    (async () => {
        try {
            const [settings, allDocks] = await Promise.all([
                getGeneralSettings(tenantId),
                getAllDocks(tenantId)
            ]);

            // Scope Filter Docks
            let docks = allDocks;
            if (scope && !scope.isFullAccess) {
                docks = allDocks.filter(d => {
                    if (scope.dockIds.length > 0 && !scope.dockIds.includes(d.id)) return false;
                    if (scope.doorGroupIds.length > 0 && !scope.doorGroupIds.includes(d.doorGroupId)) return false;
                    if (scope.factoryIds.length > 0 && !scope.factoryIds.includes(d.factoryId)) return false;
                    return true;
                });
            }

            if (isCancelled) return;

            const slotsRef = collection(db, `tenants/${tenantId}/${COLLECTIONS.RESERVATION_SLOTS}`);
            const todayStart = new Date();
            todayStart.setHours(0, 0, 0, 0);
            const todayEnd = new Date();
            todayEnd.setHours(23, 59, 59, 999);
            const now = new Date();

            // Fetch today's reservations for accurate stats and efficiency
            const statsQuery = query(
                slotsRef,
                where('startTime', '>=', Timestamp.fromDate(todayStart)),
                where('startTime', '<=', Timestamp.fromDate(todayEnd))
            );

            currentUnsubscribe = onSnapshot(statsQuery, (snapshot) => {
                let pending = 0;
                let entered = 0;
                let delayed = 0;
                let reservedSlotsCount = 0;

                snapshot.forEach((doc) => {
                    const data = doc.data();
                    const startTime = data.startTime?.toDate();

                    // Scope Filter
                    if (scope && !scope.isFullAccess) {
                        if (scope.factoryIds.length > 0 && !scope.factoryIds.includes(data.factoryId)) return;
                    }

                    const status = data.status || '';

                    const isEntered = [
                        VEHICLE_STATUS.FACILITY_ENTERED,
                        VEHICLE_STATUS.IN_PARKING,
                        VEHICLE_STATUS.LOADING_STARTED,
                        VEHICLE_STATUS.LOADING_COMPLETED,
                        VEHICLE_STATUS.FACILITY_EXITED,
                        'entered', 'parking', 'loading', 'completed', 'exited', 'loading_start', 'loading_end'
                    ].includes(status);

                    const isCancelled = status === VEHICLE_STATUS.CANCELLED;

                    if (!isCancelled) {
                        if (isEntered) {
                            entered++;
                        } else {
                            // Basic pending (not entered yet)
                            pending++;

                            // Logic for delay: If startTime is in the past and not entered
                            if (startTime && startTime < now) {
                                delayed++;
                            }
                        }

                        // Count for efficiency (all planned slots for today)
                        reservedSlotsCount++;
                    }
                });

                // --- Dynamic Efficiency Calculation ---
                let efficiency = 0;
                if (settings && docks && docks.length > 0) {
                    const startH = parseInt((settings.operationStartHour || '08:00').split(':')[0], 10);
                    const endH = parseInt((settings.operationEndHour || '18:00').split(':')[0], 10);
                    const slotDuration = settings.slotDuration || 30;

                    const workingMinutes = (endH - startH) * 60;
                    if (workingMinutes > 0) {
                        const slotsPerDock = workingMinutes / slotDuration;
                        const totalAvailableDailySlots = Math.floor(slotsPerDock * docks.length);

                        if (totalAvailableDailySlots > 0) {
                            efficiency = Math.round((reservedSlotsCount / totalAvailableDailySlots) * 100);
                            efficiency = Math.min(efficiency, 100);
                        }
                    }
                }

                callback({
                    pendingReservations: pending,
                    vehiclesAtSite: entered,
                    delayedReservations: delayed,
                    efficiency: efficiency
                });
            }, (error) => {
                console.error('Stats Subscription Error:', error);
            });

        } catch (error) {
            console.error('Setup Stats Error:', error);
            callback({ pendingReservations: 0, vehiclesAtSite: 0, delayedReservations: 0, efficiency: 0 });
        }
    })();

    return () => {
        isCancelled = true;
        if (currentUnsubscribe) currentUnsubscribe();
    };
};

/**
 * Subscribe to recent activities (Real-time)
 */
/**
 * Subscribe to recent activities (Real-time)
 * Supports date filtering and limit
 */
export const subscribeToRecentActivities = (tenantId, callback, limitCount = 5, startDate = null, scope = null) => {
    if (!tenantId) {
        callback([]);
        return () => { };
    }

    try {
        const activityRef = collection(db, `tenants/${tenantId}/${COLLECTIONS.RESERVATION_SLOTS}`);

        let q;
        const constraints = [
            orderBy('updatedAt', 'desc') // Sort by time desc (current -> past)
        ];

        if (startDate) {
            constraints.push(where('updatedAt', '>=', startDate));
        }

        if (limitCount) {
            constraints.push(limit(limitCount));
        }

        q = query(activityRef, ...constraints);

        const unsubscribe = onSnapshot(q, (snapshot) => {
            let activities = snapshot.docs.map(doc => {
                const data = doc.data();
                const statusLabel = STATUS_LABELS[data.status] || data.statusLabel || data.status;
                return {
                    id: doc.id,
                    time: data.updatedAt?.toDate().toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }) || '--:--',
                    plateNumber: data.plateNumber || 'PLAKA YOK',
                    transporterName: data.transporterName || 'Firma Belirsiz',
                    status: data.status,
                    statusLabel: statusLabel,
                    dockId: data.dockId,
                    factoryId: data.factoryId,
                    dockName: data.dockName || data.dock?.name || 'Rampa Atanmadı',
                    responsible: data.updatedBy || 'Sistem'
                };
            });

            // Scope Filter
            if (scope && !scope.isFullAccess && scope.factoryIds.length > 0) {
                activities = activities.filter(a => scope.factoryIds.includes(a.factoryId));
            }

            callback(activities);
        }, (error) => {
            console.error('Activity Subscription Error:', error);
        });

        return unsubscribe;
    } catch (error) {
        console.error('Setup Activity Error:', error);
        return () => { };
    }
};

/**
 * Subscribe to detailed daily statistics for charts
 */
export const subscribeToDailyStats = (tenantId, callback, scope = null) => {
    if (!tenantId) {
        callback({ statusBreakdown: [], transporterStats: [] });
        return () => { };
    }

    try {
        const slotsRef = collection(db, `tenants/${tenantId}/${COLLECTIONS.RESERVATION_SLOTS}`);

        const startOfDay = new Date();
        startOfDay.setHours(0, 0, 0, 0);

        const endOfDay = new Date();
        endOfDay.setHours(23, 59, 59, 999);


        // Query for today's reservations using timestamp range
        const q = query(
            slotsRef,
            where('startTime', '>=', Timestamp.fromDate(startOfDay)),
            where('startTime', '<=', Timestamp.fromDate(endOfDay))
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const statusCounts = {
                pending: 0,
                facility_entered: 0,
                in_parking: 0,
                dock_occupied: 0,
                loading_completed: 0,
                facility_exited: 0
            };

            const transporterMap = {};
            const hourlyCounts = new Array(24).fill(0);
            const dockMap = {};

            snapshot.forEach((doc) => {
                const data = doc.data();

                // Scope Filter
                if (scope && !scope.isFullAccess && scope.factoryIds.length > 0) {
                    if (!scope.factoryIds.includes(data.factoryId)) return;
                }

                const status = data.status || '';
                const transporterName = data.transporterName || 'Bilinmeyen Firma';
                const startTime = data.startTime?.toDate();
                const now = new Date();

                const isEntered = [
                    VEHICLE_STATUS.FACILITY_ENTERED,
                    VEHICLE_STATUS.IN_PARKING,
                    VEHICLE_STATUS.LOADING_STARTED,
                    VEHICLE_STATUS.LOADING_COMPLETED,
                    VEHICLE_STATUS.FACILITY_EXITED,
                    'entered', 'parking', 'loading', 'completed', 'exited'
                ].includes(status);

                const isCancelled = status === VEHICLE_STATUS.CANCELLED;

                // Comprehensive Status Mapping
                if ([
                    VEHICLE_STATUS.PENDING_APPROVAL,
                    VEHICLE_STATUS.APPROVED,
                    VEHICLE_STATUS.TRANSPORTER_REVISED,
                    'pending', 'approved', 'confirmed', 'transporter_confirmed', 'waiting_reservation'
                ].includes(status)) {
                    statusCounts.pending++;
                } else if (status === VEHICLE_STATUS.FACILITY_ENTERED || status === 'entered') {
                    statusCounts.facility_entered++;
                } else if (status === VEHICLE_STATUS.IN_PARKING || status === 'parking') {
                    statusCounts.in_parking++;
                } else if (status === VEHICLE_STATUS.LOADING_STARTED || status === 'loading' || status === 'loading_start') {
                    statusCounts.dock_occupied++;
                } else if (status === VEHICLE_STATUS.LOADING_COMPLETED || status === 'completed' || status === 'loading_end') {
                    statusCounts.loading_completed++;
                } else if (status === VEHICLE_STATUS.FACILITY_EXITED || status === 'exited') {
                    statusCounts.facility_exited++;
                }

                // Transporter Stats
                if (!transporterMap[transporterName]) {
                    transporterMap[transporterName] = {
                        name: transporterName,
                        total: 0,
                        delayed: 0,
                        waiting: 0,
                        onTime: 0
                    };
                }

                if (!isCancelled) {
                    transporterMap[transporterName].total++;

                    if (isEntered) {
                        transporterMap[transporterName].onTime++;
                    } else {
                        // Not entered yet
                        if (startTime && startTime < now) {
                            transporterMap[transporterName].delayed++;
                        } else {
                            transporterMap[transporterName].waiting++;
                        }
                    }
                }

                // Track hourly density based on startTime and endTime duration (ignoring cancelled)
                if (!isCancelled && data.startTime && data.endTime) {
                    const start = data.startTime.toDate ? data.startTime.toDate() : new Date(data.startTime);
                    const end = data.endTime.toDate ? data.endTime.toDate() : new Date(data.endTime);

                    if (!isNaN(start) && !isNaN(end)) {
                        const startHour = start.getHours();
                        const endHour = end.getHours();

                        // Increment counts for each hour the vehicle is scheduled to be present
                        for (let h = startHour; h <= endHour; h++) {
                            if (h >= 0 && h < 24) {
                                hourlyCounts[h]++;
                            }
                        }
                    }
                }


                // Removed previous hourlyCounts calculation based on single reservationTime point

                if (data.dockName) {
                    dockMap[data.dockName] = (dockMap[data.dockName] || 0) + 1;
                }
            });

            // Calculate total based on mapped counts
            const totalVehiclesCount = statusCounts.pending + statusCounts.facility_entered + statusCounts.in_parking + statusCounts.dock_occupied + statusCounts.loading_completed + statusCounts.facility_exited;

            const statusBreakdown = [
                { name: 'Bekleyen', value: statusCounts.pending, color: STATUS_COLORS[VEHICLE_STATUS.PENDING_APPROVAL] },
                { name: 'Tesis İçi', value: statusCounts.facility_entered, color: STATUS_COLORS[VEHICLE_STATUS.FACILITY_ENTERED] },
                { name: 'Park Alanı', value: statusCounts.in_parking, color: STATUS_COLORS[VEHICLE_STATUS.IN_PARKING] },
                { name: 'Rampada', value: statusCounts.dock_occupied, color: STATUS_COLORS[VEHICLE_STATUS.LOADING_STARTED] },
                { name: 'Yükleme Bitti', value: statusCounts.loading_completed, color: STATUS_COLORS[VEHICLE_STATUS.LOADING_COMPLETED] },
                { name: 'Tesisten Çıktı', value: statusCounts.facility_exited, color: STATUS_COLORS[VEHICLE_STATUS.FACILITY_EXITED] }
            ];

            // 2. Transporter Bar Data (Top 5 active)
            const transporterStats = Object.values(transporterMap)
                .sort((a, b) => b.total - a.total)
                .slice(0, 5);

            // 3. Hourly Traffic Data
            const hourlyTraffic = hourlyCounts.map((count, hour) => ({
                name: `${hour}:00`,
                count: count
            }));

            // 4. Dock Utilization Data
            const dockUtilization = Object.keys(dockMap).map(dockName => ({
                name: dockName,
                count: dockMap[dockName]
            })).sort((a, b) => b.count - a.count).slice(0, 8); // Top 8 docks

            callback({
                statusBreakdown,
                transporterStats,
                hourlyTraffic,
                dockUtilization,
                totalVehicles: totalVehiclesCount
            });
        }, (error) => {
            console.error('Daily Stats Subscription Error:', error);
        });

        return unsubscribe;
    } catch (error) {
        console.error('Setup Daily Stats Error:', error);
        return () => { };
    }
};
